import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { GoLinkExternal } from 'react-icons/go';
import { Container } from './styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/auth';
import SessionTimeOut from '../SessionTimeout';
import ModalReadConsent from '../ReadConsentModal';
import Api, { exceptionNotificationAPI } from '../../services/api';
import OperationsList from './OperationsList';
import TableLoading from '../TableLoading';

function DashboardLocal() {
    const navigate = useNavigate();
    const { user, userRoles } = useAuth();
    
    const [userObject, setUserObject] = useState(null);
    const [modalReadShow, setModalReadShow] = useState(false);
    const [data, setData] = useState([]);
    const [selectedCrop, setSelectedCrop] = useState("");
    const [selectedCulture, setSelectedCulture] = useState("");
    const [crops, setCrops] = useState([]);
    const [loading, setLoading] = useState(false);

    // Load initial data
    useEffect(() => {
        const userObjectAux = JSON.parse(localStorage.getItem('@Thrive:user'));

        if (userObjectAux) {
            if (userRoles.includes('RoleApprover') && userObjectAux.needReadConsent && userObjectAux.isFirstLogin) {
                setModalReadShow(true);
            }
        }

        setUserObject(userObjectAux);
        setData([]);
        loadCrops(userObjectAux);
    }, [userRoles]);

    // Load crops
    const loadCrops = useCallback(async (userObjectAux) => {
        setLoading(true);
        try {
            const endpoint = `Dashboard/getAllYears?selectedCulture=${userObjectAux.selectedCultureID}`;
            const result = await Api.get(endpoint);
            
            if (result && result.data) {
                setCrops(result.data.response);
                
                if (userObjectAux != null && userObjectAux.selectedReportYearID) {
                    setSelectedCrop(userObjectAux.selectedReportYearID);
                } else {
                    setSelectedCrop(result.data.response[0].reportYearID);
                }
                
                const cultureStoraged = localStorage.getItem('@Thrive:cultureID');
                setSelectedCulture(cultureStoraged);
            }
        } catch (error) {
            exceptionNotificationAPI(error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Load filtered data
    useEffect(() => {
        if (selectedCrop === "" || selectedCulture === "") return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const endpoint = `Dashboard/GetByFilter?selectedReportYear=${selectedCrop}&selectedCulture=${selectedCulture}`;
                const result = await Api.get(endpoint);
                
                if (result && result.data) {
                    setData(result.data.response);
                }

                // Update user object if crop changed
                if (userObject && userObject.selectedReportYearID && userObject.selectedReportYearID !== selectedCrop) {
                    const targetCrop = crops.find(x => x.reportYearID === parseInt(selectedCrop));
                    userObject.selectedReportYearID = parseInt(selectedCrop);
                    userObject.selectedReportYear = targetCrop ? targetCrop.year : "";
                    localStorage.setItem('@Thrive:user', JSON.stringify(userObject));
                }
            } catch (error) {
                exceptionNotificationAPI(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedCrop, selectedCulture, userObject, crops]);

    // Memoized callbacks
    const handleModuleClick = useCallback((operationName, module) => {
        let url;
        if (module.cultureTypeID != null) {
            url = `/report/${operationName.replaceAll(' ', '').replaceAll('&', '').toLowerCase()}/${module.description.replaceAll(' ', '').replaceAll('&', '').toLowerCase()}/${module.cultureTypeID}`;
        } else {
            if (module.moduleID === 24 || module.moduleID === 25 || module.moduleID === 26 || module.moduleID === 27) {
                url = `/${operationName.replaceAll(' ', '').replaceAll('&', '').toLowerCase()}/${module.description.replaceAll(' ', '').replaceAll('&', '').toLowerCase()}`;
            } else {
                url = `/report/${operationName.replaceAll(' ', '').replaceAll('&', '').toLowerCase()}/${module.description.replaceAll(' ', '').replaceAll('&', '').toLowerCase()}`;
            }
        }
        navigate(url);
    }, [navigate]);

    const handleCompareVersion = useCallback((companyID, reportYearID, operationID, moduleID) => {
        navigate(`/compareVersion/${companyID}/${reportYearID}/${operationID}/${moduleID}`);
    }, [navigate]);

    const handleDiscussionClick = useCallback((companyID, reportYearID, operationID, moduleID) => {
        navigate(`/discussionDashboard/${companyID}/${reportYearID}/${operationID}/${moduleID}`);
    }, [navigate]);

    const handleRedirectBI = useCallback(() => {
        window.open("https://app.powerbi.com/groups/me/apps/257047f9-5a20-4cc4-8fc8-46f7ba84d10c/reports/d5f328ee-17cb-44ba-805f-91ff77e4349b/ReportSection650d75efb75b006da952?ctid=ff9c7474-421d-4957-8d47-c4b64dec87b5&experience=power-bi", "_blank");
    }, []);

    const handleCropChange = useCallback((value) => {
        const userStoraged = JSON.parse(localStorage.getItem('@Thrive:user'));
        if (!userStoraged) {
            setSelectedCrop(value);
            return;
        }
        const reportYearID = parseInt(value, 10);
        const targetCrop = crops.find((x) => x.reportYearID === reportYearID);
        userStoraged.selectedReportYearID = reportYearID;
        userStoraged.selectedReportYear = targetCrop ? targetCrop.year : '';
        localStorage.setItem('@Thrive:user', JSON.stringify(userStoraged));
        window.location.reload();
    }, [crops]);

    // Check if should show BI button
    const shouldShowBIButton = useMemo(() => {
        return userObject && userObject.isSourcingGroup === false;
    }, [userObject]);

    return (
        <Container>
            <SessionTimeOut />
            
            <div className="dashboard-header">
                <div className="container">
                    <div className="row">
                        <div className="dashboard-title col-md-8">
                            {userObject && <h1>{userObject.company.name}</h1>}
                        </div>
                        <div className="dashboard-filter col-md-4">
                            {!loading && (
                                <select 
                                    className="col-md-12" 
                                    onChange={(e) => handleCropChange(e.target.value)} 
                                    value={selectedCrop}
                                >
                                    {crops && crops.map(crop => (
                                        <option key={crop.reportYearID} value={crop.reportYearID}>
                                            {crop.year}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="container dashboard-body">
                {data && data.length > 0 ? (
                    loading ? (
                        <TableLoading />
                    ) : (
                        <OperationsList
                            data={data}
                            userObject={userObject}
                            onModuleClick={handleModuleClick}
                            onCompareVersion={handleCompareVersion}
                            onDiscussionClick={handleDiscussionClick}
                        />
                    )
                ) : (
                    loading ? (
                        <TableLoading />
                    ) : (
                        <div>There is no data to list here</div>
                    )
                )}
            </div>
            
            {/* {shouldShowBIButton && (
                <div className="container dashboard-buttons">
                    <button 
                        type="button" 
                        onClick={handleRedirectBI} 
                        className="btn btn-custom"
                    >
                        see dashboard <GoLinkExternal size={17} />
                    </button>
                </div>
            )} */}

            <ModalReadConsent
                show={modalReadShow}
                onHide={() => setModalReadShow(false)}
                setModalReadShow={setModalReadShow}
                userObject={userObject}
            />
        </Container>
    );
}

export default DashboardLocal;
